import React from 'react';
import './StatusPill.scss';

/*
 * StatusPill — Module UI Guide §13 status vocabulary.
 * Status is a coloured DOT beside its WORD, never colour alone. Nine tones,
 * one word each. error/offline pulse (an operator must act); idle is neutral
 * and never pulses. Severity/status colours are full-chroma and deliberately
 * separate from the brand accent (Law 4).
 *
 * Usage:
 *   <StatusPill tone="ok" label="Active" />
 *   <StatusPill tone="error">Failed</StatusPill>
 *   <StatusPill status={rawValue} />   // resolved via statusTone()
 */

export const STATUS_TONES = {
	ok: { label: 'OK', pulses: false },
	degraded: { label: 'Degraded', pulses: false },
	error: { label: 'Error', pulses: true },
	offline: { label: 'Offline', pulses: true },
	idle: { label: 'Idle', pulses: false },
	pending: { label: 'Pending', pulses: false },
	none: { label: '', pulses: false },
};

// Canonical resolver: map a raw/legacy value to exactly one tone. Keep ALL
// status mapping here so a badge, a filter and a count never disagree (Law 17).
export const statusTone = (raw) => {
	if (raw === null || raw === undefined) return 'none';
	const v = String(raw).trim().toLowerCase();
	switch (v) {
		case 'ok':
		case 'active':
		case 'healthy':
		case 'success':
		case 'completed':
		case 'enabled':
		case 'online':
		case 'connected':
		case 'verified':
			return 'ok';
		case 'degraded':
		case 'warning':
		case 'warn':
			return 'degraded';
		case 'error':
		case 'failed':
		case 'failure':
		case 'rejected':
			return 'error';
		case 'offline':
		case 'disconnected':
		case 'unreachable':
			return 'offline';
		case 'idle':
		case 'inactive':
		case 'disabled':
		case 'stopped':
			return 'idle';
		case 'pending':
		case 'paused':
		case 'queued':
		case 'scheduled':
		case 'processing':
		case 'draft':
			return 'pending';
		case '':
		case 'none':
		case 'n/a':
			return 'none';
		default:
			return 'pending';
	}
};

const StatusPill = ({ tone, status, label, children, className = '' }) => {
	const resolved = tone && STATUS_TONES[tone] ? tone : statusTone(status);
	const meta = STATUS_TONES[resolved] || STATUS_TONES.none;
	const text = label ?? children ?? meta.label;

	return (
		<span className={`ivs-status ivs-status--${resolved} ${className}`.trim()}>
			{resolved !== 'none' && (
				<i className={`ivs-status__dot${meta.pulses ? ' ivs-status__dot--pulse' : ''}`} aria-hidden="true" />
			)}
			<span className="ivs-status__label">{text}</span>
		</span>
	);
};

export default StatusPill;
